import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';
import { ref } from 'vue';

export function useScriptSureApi() {
    const loading = ref(false);
    const error = ref(null);
    const authStore = useAuthStore();

    const getAccountUrl = () => `/api/account/${authStore.account.id}/scriptsure`;

    /**
     * Check if account has ScriptSure configured and return current status
     */
    const hasScriptSureAccount = async () => {
        const response = await $http.get(`${getAccountUrl()}/has-account`);
        return response.data;
    };

    /**
     * Toggle ScriptSure enabled/disabled
     */
    const toggleAccount = async () => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/toggle-account`);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure toggle account error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Complete ScriptSure sign-in (3-step process):
     * 1. Authenticate to get session token
     * 2. Set prescriber context with practiceId and prescriberId
     * 3. Register patient with ScriptSure and save patientId to FHIR Patient
     * @param {Object} data - The sign-in data including practiceId, prescriberId, and patient data
     * @returns {Promise<Object>} The sign-in response including scriptSurePatientId
     */
    const signIn = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/sign-in`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure sign-in error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Sync a diagnosis to ScriptSure
     * Automatically gets or creates an encounter before posting the diagnosis
     * @param {Object} data - The diagnosis data
     * @param {number} data.patientId - The ScriptSure patient ID (required)
     * @param {string} data.conceptId - The diagnosis code, e.g. "S93.4" (required)
     * @param {number} data.codingSystem - 0=Unknown, 1=ICD9, 2=ICD10 (required)
     * @param {string} data.name - Title for the diagnosis (required)
     * @param {boolean} data.archive - false=active, true=archived (required)
     * @returns {Promise<Object>} The sync response including encounterId
     */
    const syncDiagnosis = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/diagnosis`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure sync diagnosis error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Update an existing diagnosis in ScriptSure
     * Automatically gets or creates an encounter before updating the diagnosis
     * @param {Object} data - The diagnosis data
     * @param {number} data.patientId - The ScriptSure patient ID (required)
     * @param {number} data.id - The ScriptSure diagnosis ID (required for updates)
     * @param {number} data.archive - 0=active, 1=archived (required)
     * @param {string} [data.conceptId] - The diagnosis code, e.g. "S93.4"
     * @param {number} [data.codingSystem] - 0=Unknown, 1=ICD9, 2=ICD10
     * @param {string} [data.name] - Title for the diagnosis
     * @returns {Promise<Object>} The update response including encounterId and diagnosis
     */
    const updateDiagnosis = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.put(`${getAccountUrl()}/diagnosis`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure update diagnosis error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Search for allergens in ScriptSure's FDB (First Data Bank) codeset
     * Returns allergy identifiers (HIC_SEQN, GCN_SEQNO, DAM_AGCSP) with allergyType
     * needed for proper drug-allergy interaction checking
     * @param {string} name - The allergen name to search for
     * @returns {Promise<Array>} Array of FDB allergen results with allergyType, allergyId, ROUTED_MED_ID, and Descr
     */
    const searchAllergen = async (name) => {
        const response = await $http.post(`${getAccountUrl()}/allergy/search`, { name });
        return response.data?.data || [];
    };

    /**
     * Sync an allergy to ScriptSure
     * Automatically gets or creates an encounter before posting the allergy
     * @param {Object} data - The allergy data
     * @param {number} data.patientId - The ScriptSure patient ID (required)
     * @param {string} data.allergyId - The allergy identifier (HIC_SEQN, DAM_ALRGN_GRP, GCN_SEQN, rxnorm, or ndc) (required)
     * @param {number} data.allergyType - 0=Singular Chemical, 1=Brand, 2=Generic, 3=Drug Class, 4=RxNorm, 5=NDC, 100=No Known Allergies (required)
     * @param {string} data.adverseEventCode - SNOMED code for adverse event type (required)
     * @param {string} data.name - Title for the allergy (required)
     * @param {number} data.archive - 0=active, 1=archived (required)
     * @returns {Promise<Object>} The sync response including encounterId and allergy with id
     */
    const syncAllergy = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/allergy`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure sync allergy error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Update an existing allergy in ScriptSure
     * Automatically gets or creates an encounter before updating the allergy
     * @param {Object} data - The allergy data
     * @param {number} data.patientId - The ScriptSure patient ID (required)
     * @param {number} data.id - The ScriptSure allergy ID (required for updates)
     * @param {string} data.allergyId - The allergy identifier (HIC_SEQN, DAM_ALRGN_GRP, GCN_SEQN, rxnorm, or ndc) (required)
     * @param {number} data.allergyType - 0=Singular Chemical, 1=Brand, 2=Generic, 3=Drug Class, 4=RxNorm, 5=NDC, 100=No Known Allergies (required)
     * @param {string} data.adverseEventCode - SNOMED code for adverse event type (required)
     * @param {string} data.name - Title for the allergy (required)
     * @param {number} data.archive - 0=active, 1=archived (required)
     * @returns {Promise<Object>} The update response including encounterId and allergy
     */
    const updateAllergy = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.put(`${getAccountUrl()}/allergy`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure update allergy error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Search for drugs in ScriptSure's FDB (First Data Bank) codeset
     * Returns drug results with FDB identifiers (ROUTED_MED_ID, GCN_SEQNO, etc.)
     * needed for building prescription payloads
     * @param {string} term - The drug name to search for
     * @returns {Promise<Array>} Array of FDB drug results with DrugGroup, GenericName, ROUTED_MED_ID, GCN_SEQNO, MED_ROUTED_MED_ID_DESC
     */
    const searchDrug = async (term) => {
        const response = await $http.post(`${getAccountUrl()}/drug/search`, { term });
        return response.data?.data?.drugs || [];
    };

    /**
     * Sync medications to ScriptSure
     * Pushes medication data and reconciliation fields to ScriptSure.
     * Creates new prescriptions for meds without a scriptSurePrescriptionId, updates existing ones.
     * @param {Object} data - The sync data
     * @param {string} data.patientId - The ScriptSure patient ID (required)
     * @param {Array} data.medications - Array of medications to sync (required)
     * @param {string} data.medications[].drugName - Medication name (required)
     * @param {string} data.medications[].status - FHIR status (required)
     * @param {string} data.medications[].reconcileDate - ISO datetime of most recent reconciliation (required)
     * @param {string} [data.medications[].scriptSurePrescriptionId] - ScriptSure prescription ID (if updating existing)
     * @param {string} [data.medications[].rxnorm] - RxNorm code
     * @param {string} [data.medications[].ndc] - NDC code
     * @param {string} [data.medications[].dose] - Dosage text
     * @param {string} [data.medications[].frequency] - Frequency text
     * @param {string} [data.medications[].startDate] - Effective start date
     * @param {string} [data.medications[].endDate] - Effective end date
     * @returns {Promise<Object>} The sync response with results (including prescriptionId for new meds) and errors
     */
    const syncMedications = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/sync-medications`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure sync medications error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Update an existing patient's demographics in ScriptSure
     * @param {Object} data - The patient data including scriptSurePatientId, practiceId, prescriberId, and demographic fields
     * @returns {Promise<Object>} The update response
     */
    const updatePatient = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.put(`${getAccountUrl()}/update-patient`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure update patient error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Get an authenticated ScriptSure messages widget URL.
     * Opens the messages inbox where practitioners can view and act on pending prescriptions.
     * @returns {Promise<Object>} The response including messagesUrl
     */
    const getMessagesUrl = async () => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/messages-url`);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure get messages URL error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Generate a ScriptSure invitation link for a practitioner at a specific location.
     * The backend determines the correct link type based on whether the location
     * already has a ScriptSure practice ID and whether the user is a provider.
     * @param {string} locationId - The FHIR Location ID
     * @param {boolean} isProvider - Whether the practitioner is a prescriber
     * @returns {Promise<Object>} The response including url and linkType
     */
    const generateInviteLink = async (locationId, isProvider = true) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/invite-link`, {
                locationId,
                isProvider,
            });
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ScriptSure generate invite link error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Get pending message count from ScriptSure for a specific prescriber/practice.
     * @param {number|string} prescriberId - The ScriptSure prescriber (doctor) ID
     * @param {number|string} practiceId - The ScriptSure practice ID
     * @returns {Promise<number>} The total pending message count
     */
    const getMessageCount = async (prescriberId, practiceId) => {
        const response = await $http.post(`${getAccountUrl()}/message-count`, {
            prescriberId,
            practiceId,
        });
        return response.data?.total || 0;
    };

    /**
     * Request ePrescribe access for a provider.
     * Creates a Freshdesk ticket and optionally triggers Zapier webhook (first provider).
     * @param {Object} data - The request data
     * @param {string} data.practitionerId - The FHIR Practitioner ID
     * @param {string} data.locationId - The FHIR Location ID
     * @param {string} data.ePrescribeType - One of: 'ePrescribe', 'ePrescribe controlled substances', 'ePrescribe proxy'
     * @returns {Promise<Object>} The response including success status and details
     */
    const requestEPrescribeAccess = async (data) => {
        loading.value = true;
        error.value = null;
        try {
            const response = await $http.post(`${getAccountUrl()}/request-eprescribe`, data);
            return response.data;
        } catch (err) {
            error.value = err;
            console.error('ePrescribe access request error:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Get the e-prescribe request status for a practitioner
     * @param {string} practitionerId - The FHIR Practitioner ID
     * @returns {Promise<Object>} The status response including exists, status, ePrescribeType
     */
    const getEPrescribeStatus = async (practitionerId) => {
        const response = await $http.get(`${getAccountUrl()}/eprescribe-status/${practitionerId}`);
        return response.data;
    };

    /**
     * Record that a practitioner's ePrescribe access has been deactivated.
     * Call after the Practitioner save has persisted a cleared scriptSurePrescriberId.
     * @param {string} practitionerId - The FHIR Practitioner ID
     * @returns {Promise<Object>} Response including deactivatedDate when recorded
     */
    const deactivateEPrescribeAccess = async (practitionerId) => {
        const response = await $http.post(`${getAccountUrl()}/deactivate-eprescribe`, { practitionerId });
        return response.data;
    };

    /**
     * Record that a practitioner's ePrescribe access has been activated.
     * Call after the Practitioner save has persisted a newly populated scriptSurePrescriberId.
     * @param {string} practitionerId - The FHIR Practitioner ID
     * @returns {Promise<Object>} Response including the new status when recorded
     */
    const activateEPrescribeAccess = async (practitionerId) => {
        const response = await $http.post(`${getAccountUrl()}/activate-eprescribe`, { practitionerId });
        return response.data;
    };

    return {
        loading,
        error,
        hasScriptSureAccount,
        toggleAccount,
        signIn,
        syncDiagnosis,
        updateDiagnosis,
        searchAllergen,
        searchDrug,
        syncAllergy,
        updateAllergy,
        syncMedications,
        updatePatient,
        generateInviteLink,
        getMessagesUrl,
        getMessageCount,
        requestEPrescribeAccess,
        getEPrescribeStatus,
        deactivateEPrescribeAccess,
        activateEPrescribeAccess,
    };
}
